import { useConvex } from "convex/react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useToast } from "@/hooks/use-toast";
import { useEditorStore } from "@/store/editorStore";
import { useVariablesStore } from "@/store/variablesStore";
import type { Tables } from "@/types/database";
import { api } from "../../../convex/_generated/api";
import { isElectron } from "@/services/storage/LocalFileSystemClient";
import { createHotspot as createLocalHotspot } from "@/data/adapters/local/hotspots";
import { sanitizeHotspotHTML, sanitizeSvgMarkup } from "@/utils/svgSanitizer";

const MIN_SCALE = 0.2;
const MAX_SCALE = 8;
const HOTSPOT_BOX_SIZE = 44;
const HANDLE_SIZE = 10;

const HANDLE_OFFSETS: Array<{ key: string; left: string; top: string; cursor: string }> = [
  { key: "nw", left: "0%", top: "0%", cursor: "nwse-resize" },
  { key: "n", left: "50%", top: "0%", cursor: "ns-resize" },
  { key: "ne", left: "100%", top: "0%", cursor: "nesw-resize" },
  { key: "w", left: "0%", top: "50%", cursor: "ew-resize" },
  { key: "e", left: "100%", top: "50%", cursor: "ew-resize" },
  { key: "sw", left: "0%", top: "100%", cursor: "nesw-resize" },
  { key: "s", left: "50%", top: "100%", cursor: "ns-resize" },
  { key: "se", left: "100%", top: "100%", cursor: "nwse-resize" },
];

type Hotspot = Tables<"hotspots">;

type ImageSize = { width: number; height: number };
type Point = { x: number; y: number };

type HotspotPosition = { x: number; y: number; z?: number };
type HotspotContent = Record<string, unknown>;
type ResizeHandle = "nw" | "n" | "ne" | "w" | "e" | "sw" | "s" | "se";

const clamp = (value: number, min: number, max: number) => Math.min(max, Math.max(min, value));

const getHotspotPosition = (hotspot: Hotspot): HotspotPosition | null => {
  const position = hotspot.position as HotspotPosition | null | undefined;
  if (!position) return null;
  if (typeof position.x !== "number" || typeof position.y !== "number") return null;
  return position;
};

const getHotspotContent = (hotspot: Hotspot): HotspotContent => {
  if (hotspot.content && typeof hotspot.content === "object") {
    return hotspot.content as HotspotContent;
  }
  if (typeof hotspot.content === "string") {
    try {
      const parsed = JSON.parse(hotspot.content) as HotspotContent;
      return parsed && typeof parsed === "object" ? parsed : {};
    } catch {
      return {};
    }
  }
  return {};
};

const parseSize = (value: unknown, fallback: number): string => {
  if (typeof value === "number" && Number.isFinite(value)) return `${value}px`;
  if (typeof value === "string") {
    const trimmed = value.trim();
    if (trimmed === "" || trimmed.toLowerCase() === "auto") return `${fallback}px`;
    return trimmed;
  }
  return `${fallback}px`;
};

const getIconMarkup = (content: HotspotContent, iconVariableValue: string): string => {
  const iconOrSvg = typeof content.iconOrSvg === "string" ? content.iconOrSvg.trim() : "";
  const iconVariable = typeof content.iconVariable === "string" ? content.iconVariable.trim() : "";
  if (iconVariable && iconVariableValue) return iconVariableValue;
  return iconOrSvg;
};

const parsePixelNumber = (value: unknown, fallback: number): number => {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string") {
    const trimmed = value.trim();
    const match = trimmed.match(/^(-?\d+(?:\.\d+)?)px$/i);
    if (match) return Number(match[1]);
  }
  return fallback;
};

const parseZoomValue = (value: unknown, fallback: number): number => {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string" && value.trim().length > 0) {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) return parsed;
  }
  return fallback;
};

const normalizePosition = (position: HotspotPosition, imageSize: ImageSize): HotspotPosition => {
  if (imageSize.width <= 0 || imageSize.height <= 0) return { x: 0.5, y: 0.5, z: 0 };
  const x = position.x > 1.5 ? position.x / imageSize.width : position.x;
  const y = position.y > 1.5 ? position.y / imageSize.height : position.y;
  return {
    x: clamp(x, 0, 1),
    y: clamp(y, 0, 1),
    z: typeof position.z === "number" ? position.z : 0,
  };
};

const toScreenPoint = (
  position: HotspotPosition,
  imageSize: ImageSize,
  scale: number,
  offset: Point,
): Point => {
  const normalized = normalizePosition(position, imageSize);
  return {
    x: offset.x + normalized.x * imageSize.width * scale,
    y: offset.y + normalized.y * imageSize.height * scale,
  };
};

const clampOffsetToBounds = (
  offset: Point,
  containerSize: { width: number; height: number },
  imageSize: ImageSize,
  scale: number,
): Point => {
  const scaledWidth = imageSize.width * scale;
  const scaledHeight = imageSize.height * scale;

  let x = offset.x;
  let y = offset.y;

  if (scaledWidth <= containerSize.width) {
    x = (containerSize.width - scaledWidth) / 2;
  } else {
    x = clamp(x, containerSize.width - scaledWidth, 0);
  }

  if (scaledHeight <= containerSize.height) {
    y = (containerSize.height - scaledHeight) / 2;
  } else {
    y = clamp(y, containerSize.height - scaledHeight, 0);
  }

  return { x, y };
};

const ZoomableImageViewer = () => {
  const convex = useConvex();
  const { toast } = useToast();
  const containerRef = useRef<HTMLDivElement | null>(null);
  const imageRef = useRef<HTMLImageElement | null>(null);
  const dragStateRef = useRef<{
    pointerId: number | null;
    start: Point;
    offset: Point;
    draggingHotspotId: string | null;
    lastHotspotPosition: HotspotPosition | null;
    moved: boolean;
  }>({
    pointerId: null,
    start: { x: 0, y: 0 },
    offset: { x: 0, y: 0 },
    draggingHotspotId: null,
    lastHotspotPosition: null,
    moved: false,
  });
  const lastHotspotPointerDownRef = useRef<{ id: string | null; time: number }>({
    id: null,
    time: 0,
  });
  const suppressNextBackgroundClickRef = useRef(false);
  const resizeStateRef = useRef<{
    pointerId: number | null;
    hotspotId: string | null;
    handle: ResizeHandle | null;
    startPointer: Point;
    startWidth: number;
    startHeight: number;
    startPosition: HotspotPosition;
    startContent: HotspotContent | null;
    liveWidth: number;
    liveHeight: number;
    livePosition: HotspotPosition;
  }>({
    pointerId: null,
    hotspotId: null,
    handle: null,
    startPointer: { x: 0, y: 0 },
    startWidth: HOTSPOT_BOX_SIZE,
    startHeight: HOTSPOT_BOX_SIZE,
    startPosition: { x: 0.5, y: 0.5, z: 0 },
    startContent: null,
    liveWidth: HOTSPOT_BOX_SIZE,
    liveHeight: HOTSPOT_BOX_SIZE,
    livePosition: { x: 0.5, y: 0.5, z: 0 },
  });

  const {
    selectedPanorama,
    hotspots,
    selectedHotspot,
    setSelectedHotspot,
    addHotspot,
    updateHotspotFromViewer,
    isDraggingToAddHotspot,
    setIsDraggingToAddHotspot,
    setPendingHotspotPlacementId,
    setPlacementMode,
    project,
  } = useEditorStore();
  const { getCombinedVariables } = useVariablesStore();

  const imageUrl = selectedPanorama?.private_file_url || selectedPanorama?.file_url || "";
  const [imageSize, setImageSize] = useState<ImageSize>({ width: 0, height: 0 });
  const [scale, setScale] = useState(1);
  const [offset, setOffset] = useState<Point>({ x: 0, y: 0 });
  const [fitScale, setFitScaleState] = useState(1);
  const panoramaData =
    selectedPanorama?.data && typeof selectedPanorama.data === "object"
      ? (selectedPanorama.data as Record<string, unknown>)
      : {};
  const maxZoom = clamp(parseZoomValue(panoramaData.maxZoom, MAX_SCALE), 0.1, 50);
  const minZoom = clamp(parseZoomValue(panoramaData.minZoom, MIN_SCALE), 0.05, maxZoom - 0.01);
  const effectiveMinZoom = Math.max(minZoom, fitScale);

  const currentHotspots = useMemo(
    () => hotspots.filter((h) => (selectedPanorama ? h.panorama_id === selectedPanorama.id : false)),
    [hotspots, selectedPanorama],
  );

  const setFitScale = useCallback(
    (size: ImageSize) => {
      const container = containerRef.current;
      if (!container || size.width === 0 || size.height === 0) return;
      const rect = container.getBoundingClientRect();
      const nextFitScale = Math.min(rect.width / size.width, rect.height / size.height);
      setFitScaleState(nextFitScale);
      const nextScale = clamp(nextFitScale, Math.max(minZoom, nextFitScale), maxZoom);
      const nextOffset = {
        x: (rect.width - size.width * nextScale) / 2,
        y: (rect.height - size.height * nextScale) / 2,
      };
      setScale(nextScale);
      setOffset(nextOffset);
    },
    [maxZoom, minZoom, setScale, setOffset],
  );

  useEffect(() => {
    if (!imageRef.current) return;
    const img = imageRef.current;
    const onLoad = () => {
      const size = { width: img.naturalWidth, height: img.naturalHeight };
      setImageSize(size);
      setFitScale(size);
    };
    if (img.complete && img.naturalWidth > 0) {
      onLoad();
      return;
    }
    img.addEventListener("load", onLoad);
    return () => img.removeEventListener("load", onLoad);
  }, [imageUrl, setFitScale]);

  useEffect(() => {
    const handleResize = () => {
      if (imageSize.width === 0 || imageSize.height === 0) return;
      setFitScale(imageSize);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [imageSize, setFitScale]);

  useEffect(() => {
    setScale((current) => clamp(current, effectiveMinZoom, maxZoom));
  }, [effectiveMinZoom, maxZoom]);

  useEffect(() => {
    if (!containerRef.current || imageSize.width === 0 || imageSize.height === 0) return;
    const rect = containerRef.current.getBoundingClientRect();
    setOffset((current) =>
      clampOffsetToBounds(
        current,
        { width: rect.width, height: rect.height },
        imageSize,
        scale,
      ),
    );
  }, [imageSize, scale, effectiveMinZoom]);

  const getImageSpacePoint = useCallback(
    (clientX: number, clientY: number) => {
      const container = containerRef.current;
      if (!container || imageSize.width === 0 || imageSize.height === 0) return null;
      const rect = container.getBoundingClientRect();
      const x = (clientX - rect.left - offset.x) / scale;
      const y = (clientY - rect.top - offset.y) / scale;
      return { x, y };
    },
    [imageSize.width, imageSize.height, offset.x, offset.y, scale],
  );

  const createHotspotAt = useCallback(
    async (clientX: number, clientY: number, hotspotType?: string) => {
      if (!selectedPanorama || !project) return;
      const imagePoint = getImageSpacePoint(clientX, clientY);
      if (!imagePoint) return;
      const normalized = {
        x: clamp(imagePoint.x / imageSize.width, 0, 1),
        y: clamp(imagePoint.y / imageSize.height, 0, 1),
        z: 0,
      };

      const newHotspot = {
        project_id: selectedPanorama.project_id,
        panorama_id: selectedPanorama.id,
        name: "Hotspot",
        type: hotspotType || "html",
        position: normalized,
        scale: { x: 1, y: 1, z: 1 },
        content: {
          text: "",
          textColor: "#ffffff",
          backgroundColor: "#2196f3",
          borderColor: "#ffffff",
          borderWidth: 0,
          borderRadius: 0,
          padding: 6,
          fontSize: "24px",
          fontWeight: "700",
          textAlign: "center",
          variant: "default",
          showPointer: false,
          pointerDirection: "bottom",
          pointerSize: 8,
          pointerOverlap: 2,
          tooltipArrowDirection: "top",
          title: "Hotspot",
          description: "",
        },
        visible: true,
        order: currentHotspots.length,
      };

      let hotspotData: Hotspot;
      if (isElectron()) {
        const craftspaceId = project.craftspace_id || "default";
        const id = await createLocalHotspot({ ...newHotspot, craftspace_id: craftspaceId });
        hotspotData = { ...newHotspot, id, _id: id, _creationTime: Date.now() } as Hotspot;
      } else {
        hotspotData = (await convex.mutation(api.hotspots.create, newHotspot)) as Hotspot;
      }

      addHotspot(hotspotData);
      setSelectedHotspot(hotspotData);
      setPendingHotspotPlacementId(null);
      setPlacementMode(false);
      setIsDraggingToAddHotspot(false);
    },
    [
      addHotspot,
      convex,
      currentHotspots.length,
      getImageSpacePoint,
      imageSize.height,
      imageSize.width,
      project,
      selectedPanorama,
      setIsDraggingToAddHotspot,
      setPendingHotspotPlacementId,
      setPlacementMode,
      setSelectedHotspot,
    ],
  );

  const handleWheel = useCallback(
    (event: WheelEvent) => {
      event.preventDefault();
      if (!containerRef.current || imageSize.width === 0 || imageSize.height === 0) return;

      const delta = -event.deltaY * 0.0015;
      const nextScale = clamp(scale * (1 + delta), effectiveMinZoom, maxZoom);
      if (nextScale === scale) return;

      const rect = containerRef.current.getBoundingClientRect();
      const cursor = {
        x: event.clientX - rect.left,
        y: event.clientY - rect.top,
      };
      const imagePoint = {
        x: (cursor.x - offset.x) / scale,
        y: (cursor.y - offset.y) / scale,
      };
      const unclampedOffset = {
        x: cursor.x - imagePoint.x * nextScale,
        y: cursor.y - imagePoint.y * nextScale,
      };
      setScale(nextScale);
      setOffset(
        clampOffsetToBounds(
          unclampedOffset,
          { width: rect.width, height: rect.height },
          imageSize,
          nextScale,
        ),
      );
    },
    [effectiveMinZoom, imageSize.width, imageSize.height, maxZoom, offset.x, offset.y, scale],
  );

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    const listener = (event: WheelEvent) => handleWheel(event);
    container.addEventListener("wheel", listener, { passive: false });
    return () => container.removeEventListener("wheel", listener);
  }, [handleWheel]);

  const handlePointerDown = useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => {
      if (event.button !== 0) return;
      const container = containerRef.current;
      if (!container) return;

      const target = event.target as HTMLElement;
      const resizeHandle = target
        .closest("[data-hotspot-resize-handle]")
        ?.getAttribute("data-hotspot-resize-handle") as ResizeHandle | null;
      if (resizeHandle) {
        const hotspotId = target.closest("[data-hotspot-id]")?.getAttribute("data-hotspot-id") || null;
        if (!hotspotId) return;
        const hotspot = currentHotspots.find((item) => item.id === hotspotId);
        if (!hotspot) return;
        const content = getHotspotContent(hotspot);
        const startWidth = parsePixelNumber(content.width, HOTSPOT_BOX_SIZE);
        const startHeight = parsePixelNumber(content.height, HOTSPOT_BOX_SIZE);
        const hotspotPosition = normalizePosition(
          getHotspotPosition(hotspot) || { x: 0.5, y: 0.5, z: 0 },
          imageSize,
        );
        setSelectedHotspot(hotspot);
        resizeStateRef.current = {
          pointerId: event.pointerId,
          hotspotId,
          handle: resizeHandle,
          startPointer: { x: event.clientX, y: event.clientY },
          startWidth,
          startHeight,
          startPosition: hotspotPosition,
          startContent: content,
          liveWidth: startWidth,
          liveHeight: startHeight,
          livePosition: hotspotPosition,
        };
        container.setPointerCapture(event.pointerId);
        event.preventDefault();
        return;
      }

      const hotspotId = target.closest("[data-hotspot-id]")?.getAttribute("data-hotspot-id") || null;
      if (hotspotId) {
        const hotspot = currentHotspots.find((item) => item.id === hotspotId);
        if (hotspot) {
          setSelectedHotspot(hotspot);
          lastHotspotPointerDownRef.current = { id: hotspotId, time: Date.now() };
        }
      }

      dragStateRef.current.pointerId = event.pointerId;
      dragStateRef.current.start = { x: event.clientX, y: event.clientY };
      dragStateRef.current.offset = { ...offset };
      dragStateRef.current.draggingHotspotId = hotspotId;
      dragStateRef.current.lastHotspotPosition = null;
      dragStateRef.current.moved = false;

      container.setPointerCapture(event.pointerId);
    },
    [currentHotspots, imageSize, offset, setSelectedHotspot],
  );

  const handlePointerMove = useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => {
      if (
        resizeStateRef.current.pointerId === event.pointerId &&
        resizeStateRef.current.hotspotId &&
        resizeStateRef.current.handle
      ) {
        const resizeState = resizeStateRef.current;
        const dx = event.clientX - resizeState.startPointer.x;
        const dy = event.clientY - resizeState.startPointer.y;
        let nextWidth = resizeState.startWidth;
        let nextHeight = resizeState.startHeight;
        let centerShiftX = 0;
        let centerShiftY = 0;

        if (resizeState.handle.includes("e")) {
          nextWidth = resizeState.startWidth + dx;
          centerShiftX = dx / 2;
        }
        if (resizeState.handle.includes("w")) {
          nextWidth = resizeState.startWidth - dx;
          centerShiftX = dx / 2;
        }
        if (resizeState.handle.includes("s")) {
          nextHeight = resizeState.startHeight + dy;
          centerShiftY = dy / 2;
        }
        if (resizeState.handle.includes("n")) {
          nextHeight = resizeState.startHeight - dy;
          centerShiftY = dy / 2;
        }

        nextWidth = clamp(nextWidth, 8, 4000);
        nextHeight = clamp(nextHeight, 8, 4000);

        // Keep opposite edge/corner anchored by shifting hotspot center as size changes.
        const nextPosition: HotspotPosition = {
          x: clamp(
            resizeState.startPosition.x + centerShiftX / Math.max(1, imageSize.width),
            0,
            1,
          ),
          y: clamp(
            resizeState.startPosition.y + centerShiftY / Math.max(1, imageSize.height),
            0,
            1,
          ),
          z: resizeState.startPosition.z ?? 0,
        };

        resizeState.liveWidth = nextWidth;
        resizeState.liveHeight = nextHeight;
        resizeState.livePosition = nextPosition;

        const currentState = useEditorStore.getState();
        const currentHotspot = currentState.hotspots.find((h) => h.id === resizeState.hotspotId);
        if (currentHotspot) {
          const currentContent = getHotspotContent(currentHotspot);
          currentState.updateHotspot(resizeState.hotspotId, {
            position: nextPosition,
            content: {
              ...currentContent,
              width: `${Math.round(nextWidth)}px`,
              height: `${Math.round(nextHeight)}px`,
            },
          });
        }
        return;
      }

      if (dragStateRef.current.pointerId !== event.pointerId) return;
      const container = containerRef.current;
      if (!container) return;

      const dx = event.clientX - dragStateRef.current.start.x;
      const dy = event.clientY - dragStateRef.current.start.y;
      if (Math.abs(dx) > 2 || Math.abs(dy) > 2) {
        dragStateRef.current.moved = true;
      }

      if (dragStateRef.current.draggingHotspotId) {
        const hotspotId = dragStateRef.current.draggingHotspotId;
        const imagePoint = getImageSpacePoint(event.clientX, event.clientY);
        if (!imagePoint) return;
        const normalized: HotspotPosition = {
          x: clamp(imagePoint.x / imageSize.width, 0, 1),
          y: clamp(imagePoint.y / imageSize.height, 0, 1),
          z: 0,
        };
        dragStateRef.current.lastHotspotPosition = normalized;
        useEditorStore.getState().updateHotspot(hotspotId, { position: normalized });
        return;
      }

      const rect = container.getBoundingClientRect();
      const nextOffset = {
        x: dragStateRef.current.offset.x + dx,
        y: dragStateRef.current.offset.y + dy,
      };
      setOffset(
        clampOffsetToBounds(
          nextOffset,
          { width: rect.width, height: rect.height },
          imageSize,
          scale,
        ),
      );
    },
    [getImageSpacePoint, imageSize.height, imageSize.width, toast, updateHotspotFromViewer],
  );

  const handlePointerUp = useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => {
      if (
        resizeStateRef.current.pointerId === event.pointerId &&
        resizeStateRef.current.hotspotId
      ) {
        const container = containerRef.current;
        if (container) {
          container.releasePointerCapture(event.pointerId);
        }
        const hotspotId = resizeStateRef.current.hotspotId;
        const startContent = resizeStateRef.current.startContent || {};
        const width = `${Math.round(resizeStateRef.current.liveWidth)}px`;
        const height = `${Math.round(resizeStateRef.current.liveHeight)}px`;
        const position = resizeStateRef.current.livePosition;
        updateHotspotFromViewer(hotspotId, {
          position,
          content: { ...startContent, width, height },
        }).catch((error) => {
          console.error("Error resizing hotspot:", error);
          toast({
            variant: "destructive",
            title: "Error resizing hotspot",
            description: "Failed to update hotspot size.",
          });
        });
        resizeStateRef.current.pointerId = null;
        resizeStateRef.current.hotspotId = null;
        resizeStateRef.current.handle = null;
        resizeStateRef.current.startContent = null;
        suppressNextBackgroundClickRef.current = true;
        return;
      }

      if (dragStateRef.current.pointerId !== event.pointerId) return;
      const shouldSuppressBackgroundClick = dragStateRef.current.moved;
      const container = containerRef.current;
      if (container) {
        container.releasePointerCapture(event.pointerId);
      }
      if (dragStateRef.current.draggingHotspotId && dragStateRef.current.lastHotspotPosition) {
        const hotspotId = dragStateRef.current.draggingHotspotId;
        const position = dragStateRef.current.lastHotspotPosition;
        updateHotspotFromViewer(hotspotId, { position }).catch((error) => {
          console.error("Error updating hotspot position:", error);
          toast({
            variant: "destructive",
            title: "Error moving hotspot",
            description: "Failed to update hotspot position.",
          });
        });
      }
      dragStateRef.current.pointerId = null;
      dragStateRef.current.draggingHotspotId = null;
      dragStateRef.current.lastHotspotPosition = null;
      dragStateRef.current.moved = false;
      suppressNextBackgroundClickRef.current = shouldSuppressBackgroundClick;
    },
    [toast, updateHotspotFromViewer],
  );

  const handleDrop = useCallback(
    (event: React.DragEvent<HTMLDivElement>) => {
      event.preventDefault();
      const hotspotType = event.dataTransfer.getData("application/x-hotspot-add");
      if (hotspotType && selectedPanorama) {
        void createHotspotAt(event.clientX, event.clientY, hotspotType);
      }
    },
    [createHotspotAt, selectedPanorama],
  );

  const handleDragOver = useCallback((event: React.DragEvent<HTMLDivElement>) => {
    if (event.dataTransfer.types.includes("application/x-hotspot-add")) {
      event.preventDefault();
    }
  }, []);

  const handleClick = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      const target = event.target as HTMLElement;
      if (target.closest("[data-hotspot-id]")) return;
      if (suppressNextBackgroundClickRef.current) {
        suppressNextBackgroundClickRef.current = false;
        return;
      }
      const recentHotspotDown = lastHotspotPointerDownRef.current;
      if (recentHotspotDown.id && Date.now() - recentHotspotDown.time < 300) {
        return;
      }
      if (dragStateRef.current.moved) {
        return;
      }
      if (isDraggingToAddHotspot) {
        void createHotspotAt(event.clientX, event.clientY);
        return;
      }
      setSelectedHotspot(null);
    },
    [createHotspotAt, isDraggingToAddHotspot, setSelectedHotspot],
  );

  useEffect(() => {
    if (!isDraggingToAddHotspot) return;
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setIsDraggingToAddHotspot(false);
        setPendingHotspotPlacementId(null);
        setPlacementMode(false);
      }
    };
    window.addEventListener("keydown", handleEscape);
    return () => window.removeEventListener("keydown", handleEscape);
  }, [isDraggingToAddHotspot, setIsDraggingToAddHotspot, setPendingHotspotPlacementId, setPlacementMode]);

  if (!selectedPanorama) {
    return (
      <div className="h-full bg-gray-900 flex items-center justify-center text-gray-400 text-sm">
        Select a zoomable image to view
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      className={`h-full bg-gray-900 relative overflow-hidden${isDraggingToAddHotspot ? " cursor-crosshair" : ""}`}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      onPointerLeave={handlePointerUp}
      onDrop={handleDrop}
      onDragOver={handleDragOver}
      onClick={handleClick}
    >
      {imageUrl ? (
        <img
          ref={imageRef}
          src={imageUrl}
          alt={selectedPanorama.name || "Zoomable image"}
          className="absolute top-0 left-0 select-none"
          style={{
            transform: `translate(${offset.x}px, ${offset.y}px) scale(${scale})`,
            transformOrigin: "top left",
            maxWidth: "none",
            maxHeight: "none",
          }}
          draggable={false}
        />
      ) : (
        <div className="h-full flex items-center justify-center text-gray-400 text-sm">
          No image available
        </div>
      )}

      {currentHotspots.map((hotspot) => {
        const position = getHotspotPosition(hotspot);
        if (!position || imageSize.width === 0 || imageSize.height === 0) return null;
        const point = toScreenPoint(position, imageSize, scale, offset);
        const isSelected = selectedHotspot?.id === hotspot.id;
        const hotspotScaleFactor = clamp(1 / Math.max(scale, 0.0001), 0.2, 6);
        const content = getHotspotContent(hotspot);
        const width = parseSize(content.width, HOTSPOT_BOX_SIZE);
        const height = parseSize(content.height, HOTSPOT_BOX_SIZE);
        const backgroundColor =
          typeof content.backgroundColor === "string" ? content.backgroundColor : "#ffffff";
        const borderColor =
          typeof content.borderColor === "string" ? content.borderColor : "#0f172a";
        const borderWidth =
          typeof content.borderWidth === "number" ? content.borderWidth : 1.5;
        const borderRadius =
          typeof content.borderRadius === "number" ? content.borderRadius : 2;
        const padding =
          typeof content.padding === "number" ? `${content.padding}px` : "0px";
        const textColor =
          typeof content.textColor === "string"
            ? content.textColor
            : typeof content.color === "string"
              ? content.color
              : "#000000";
        const fontSize =
          typeof content.fontSize === "string" || typeof content.fontSize === "number"
            ? String(content.fontSize)
            : "12px";
        const fontWeight =
          typeof content.fontWeight === "number" || typeof content.fontWeight === "string"
            ? String(content.fontWeight)
            : "500";
        const textAlign =
          typeof content.textAlign === "string" ? content.textAlign : "center";
        const lineHeight =
          typeof content.lineHeight === "string" ? content.lineHeight : "1";
        const textShadow =
          typeof content.textShadow === "string" ? content.textShadow : "none";
        const iconSize =
          typeof content.iconSize === "string" || typeof content.iconSize === "number"
            ? String(content.iconSize)
            : "100%";
        const iconVariableName =
          typeof content.iconVariable === "string" ? content.iconVariable : "";
        const iconVariableValue =
          iconVariableName && project
            ? getCombinedVariables(project.craftspace_id || "default", project.id).icons.find(
                (icon) => icon.name === iconVariableName,
              )?.value || ""
            : "";
        const iconMarkup = getIconMarkup(content, iconVariableValue);
        const hasIcon = Boolean(iconMarkup);
        const hasText = typeof content.text === "string" && content.text.trim().length > 0;
        const sanitizedText = hasText ? sanitizeHotspotHTML(content.text as string) : "";
        const isSvgIcon =
          hasIcon && (iconMarkup.startsWith("<svg") || iconMarkup.startsWith("data:image/svg"));
        const iconHtml = isSvgIcon ? sanitizeSvgMarkup(iconMarkup) : iconMarkup;
        return (
          <div
            key={hotspot.id}
            data-hotspot-id={hotspot.id}
            className="absolute -translate-x-1/2 -translate-y-1/2"
            style={{
              left: point.x,
              top: point.y,
            }}
            onClick={(event) => {
              event.stopPropagation();
              setSelectedHotspot(hotspot);
            }}
          >
            <div
              style={{
                transform: `scale(${hotspotScaleFactor})`,
                transformOrigin: "center center",
              }}
            >
              <div
                className="shadow flex items-center justify-center"
                style={{
                  width,
                  height,
                  backgroundColor,
                  borderColor: isSelected ? "#60a5fa" : borderColor,
                  borderWidth: borderWidth,
                  borderRadius: borderRadius,
                  borderStyle: "solid",
                  padding,
                  color: textColor,
                  fontSize,
                  fontWeight,
                  textAlign,
                  lineHeight,
                  textShadow,
                }}
              >
                {hasIcon && isSvgIcon && (
                  <span
                    className="inline-flex items-center justify-center"
                    style={{ width: iconSize, height: iconSize }}
                    dangerouslySetInnerHTML={{ __html: iconHtml }}
                  />
                )}
                {hasIcon && !isSvgIcon && (
                  <span
                    className="inline-flex items-center justify-center"
                    style={{ fontSize: iconSize, lineHeight: 1 }}
                  >
                    {iconHtml}
                  </span>
                )}
                {hasText && !hasIcon && (
                  <span
                    className="block"
                    dangerouslySetInnerHTML={{ __html: sanitizedText }}
                  />
                )}
                {hasText && hasIcon && (
                  <span
                    className="block mt-1"
                    dangerouslySetInnerHTML={{ __html: sanitizedText }}
                  />
                )}
              </div>
              {isSelected && (
                <div className="absolute inset-0">
                  {HANDLE_OFFSETS.map((handle) => (
                    <div
                      key={handle.key}
                      data-hotspot-resize-handle={handle.key}
                      className="absolute rounded-full border-2 border-blue-100 bg-blue-500"
                      style={{
                        width: HANDLE_SIZE,
                        height: HANDLE_SIZE,
                        left: handle.left,
                        top: handle.top,
                        transform: "translate(-50%, -50%)",
                        cursor: handle.cursor,
                      }}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>
        );
      })}

      {isDraggingToAddHotspot && (
        <div className="absolute inset-x-0 bottom-4 flex justify-center text-xs text-gray-200">
          <div className="rounded bg-black/60 px-3 py-1">Click to place your hotspot</div>
        </div>
      )}
    </div>
  );
};

export default ZoomableImageViewer;
